import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Card, CardContent } from "@/components/ui/card";

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Terms of Service"
        description="Review the terms and conditions that govern your use of RunoBlog, including acceptable use, intellectual property, and account responsibilities."
        path="/terms"
      />
      <Navigation />
      
      {/* Hero Section */}
      <section className="border-b border-border bg-gradient-to-br from-primary/5 to-accent/5">
        <div className="container mx-auto px-4 py-16 md:py-24">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="mb-6 text-4xl font-bold tracking-tight md:text-5xl">
              Terms of Service
            </h1>
            <p className="text-lg text-muted-foreground">
              Last updated: November 11, 2025
            </p>
          </div>
        </div>
      </section>

      {/* Terms Content */}
      <section className="container mx-auto px-4 py-16 md:py-24">
        <div className="mx-auto max-w-4xl">
          <Card>
            <CardContent className="prose prose-slate max-w-none pt-6 dark:prose-invert">
              <div className="space-y-8">
                <div>
                  <h2 className="mb-4 text-2xl font-bold text-foreground">1. Introduction</h2>
                  <p className="text-muted-foreground">
                    Welcome to RunoBlog ("we," "us," or "our"). By accessing or using www.runoblog.site (the "Website"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree with any part of these Terms, please do not use our website.
                  </p>
                </div>

                <div>
                  <h2 className="mb-4 text-2xl font-bold text-foreground">2. Use of Our Website</h2>
                  <p className="text-muted-foreground">
                    By using this website, you agree to:
                  </p>
                  <ul className="mt-4 space-y-2 text-muted-foreground">
                    <li>Use it only for lawful purposes.</li>
                    <li>Not engage in any activity that may damage or interfere with the proper functioning of the site.</li>
                    <li>Not attempt to gain unauthorized access to our systems or data.</li>
                    <li>Not use our website to distribute malware, spam, or harmful content.</li>
                  </ul>
                  <p className="mt-4 text-muted-foreground">
                    We reserve the right to restrict or terminate your access to the website if you violate these Terms.
                  </p>
                </div>

                <div>
                  <h2 className="mb-4 text-2xl font-bold text-foreground">3. Intellectual Property Rights</h2>
                  <p className="text-muted-foreground">
                    All content on this website—including text, graphics, images, videos, code, and design—is the intellectual property of RunoBlog, unless otherwise stated.
                  </p>
                  <p className="mt-4 text-muted-foreground">
                    You may view or download content for personal, non-commercial use only. You must not reproduce, distribute, modify, or republish any content from this site without prior written permission.
                  </p>
                </div>

                <div>
                  <h2 className="mb-4 text-2xl font-bold text-foreground">4. User-Generated Content</h2>
                  <p className="text-muted-foreground">
                    If you post, comment, or submit any content to our site, you grant RunoBlog a non-exclusive, royalty-free, perpetual, and worldwide license to use, reproduce, and display that content for website and promotional purposes.
                  </p>
                  <p className="mt-4 text-muted-foreground">
                    You are solely responsible for the content you submit and must ensure it does not violate any laws, copyright, or third-party rights.
                  </p>
                </div>

                <div>
                  <h2 className="mb-4 text-2xl font-bold text-foreground">5. Links to Other Websites</h2>
                  <p className="text-muted-foreground">
                    Our website may contain links to third-party websites or services that are not owned or controlled by RunoBlog. We are not responsible for the content, policies, or practices of these third-party sites. We recommend that you review the terms and privacy policies of any external sites you visit.
                  </p>
                </div>

                <div>
                  <h2 className="mb-4 text-2xl font-bold text-foreground">6. Disclaimer of Warranties</h2>
                  <p className="text-muted-foreground">
                    This website and its content are provided "as is" and "as available" without any warranties, express or implied.
                  </p>
                  <p className="mt-4 text-muted-foreground">
                    We do not guarantee:
                  </p>
                  <ul className="mt-2 space-y-2 text-muted-foreground">
                    <li>That the website will always be available or error-free</li>
                    <li>The accuracy, reliability, or completeness of any content</li>
                    <li>That the website or its servers are free of harmful components</li>
                  </ul>
                  <p className="mt-4 text-muted-foreground">
                    Use of this website is at your own risk.
                  </p>
                </div>

                <div>
                  <h2 className="mb-4 text-2xl font-bold text-foreground">7. Limitation of Liability</h2>
                  <p className="text-muted-foreground">
                    To the fullest extent permitted by law, RunoBlog and its affiliates, employees, or agents will not be liable for any direct, indirect, incidental, or consequential damages arising from your use of our website, including but not limited to loss of data, profits, or goodwill.
                  </p>
                </div>

                <div>
                  <h2 className="mb-4 text-2xl font-bold text-foreground">8. Indemnification</h2>
                  <p className="text-muted-foreground">
                    You agree to indemnify and hold harmless RunoBlog from and against any claims, damages, losses, liabilities, and expenses (including legal fees) arising from your use of the website or violation of these Terms.
                  </p>
                </div>

                <div>
                  <h2 className="mb-4 text-2xl font-bold text-foreground">9. Privacy and Cookies</h2>
                  <p className="text-muted-foreground">
                    Your use of this website is also governed by our{" "}
                    <a href="/privacy" className="text-primary hover:underline">Privacy Policy</a>
                    {" "}and{" "}
                    <a href="/cookie-policy" className="text-primary hover:underline">Cookie Policy</a>.
                    By using this website, you consent to the collection and use of your data as described in those policies.
                  </p>
                </div>

                <div>
                  <h2 className="mb-4 text-2xl font-bold text-foreground">10. Termination</h2>
                  <p className="text-muted-foreground">
                    We may suspend or terminate your access to the website without notice if we believe you have violated these Terms or any applicable laws.
                  </p>
                  <p className="mt-4 text-muted-foreground">
                    Upon termination, all rights granted to you under these Terms will immediately cease.
                  </p>
                </div>

                <div>
                  <h2 className="mb-4 text-2xl font-bold text-foreground">11. Changes to These Terms</h2>
                  <p className="text-muted-foreground">
                    We may update these Terms of Service from time to time. Any changes will be posted on this page with an updated "Last Updated" date. Your continued use of the website after such changes constitutes acceptance of the new Terms.
                  </p>
                </div>

                <div>
                  <h2 className="mb-4 text-2xl font-bold text-foreground">12. Governing Law</h2>
                  <p className="text-muted-foreground">
                    These Terms shall be governed by and construed in accordance with the laws of Nigeria, without regard to its conflict of law principles. Any disputes arising under or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts located in Nigeria.
                  </p>
                </div>

                <div>
                  <h2 className="mb-4 text-2xl font-bold text-foreground">13. Contact Us</h2>
                  <p className="text-muted-foreground">
                    If you have any questions or concerns about these Terms, please contact us at:
                  </p>
                  <ul className="mt-4 space-y-2 text-muted-foreground">
                    <li>📧 Email: runoux.dev@gmail.com</li>
                    <li>🌐 Website: <a href="https://www.runoblog.site" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">https://www.runoblog.site</a></li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsOfService;
